import cx from 'classnames';
import {emojiBlast} from 'emoji-blast';
import {FC, lazy, useState} from 'react';
import {saveReaction} from '~/GuestReaction/api/guest';
import {history} from '~/QuickEcards/history';
import {useAppSelector} from '~/QuickEcards/store';
import {
  selectEcardHostName,
  selectEcardId,
  selectEcardIsDraft,
  selectHasGiftCard,
} from '~/QuickEcards/store/reducers/ecard';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import './ECardActionsDrawer.sass';

const GiftCardController = lazy(() =>
  import('~/QuickEcards/components/ECardActionsDrawer/GiftCardController').then((module) => ({
    default: module.GiftCardController,
  }))
);

interface ReactionButton {
  text: string;
}

const BUTTONS: ReactionButton[] = [
  {text: '🌟 Thanks!'},
  {text: '❤️ Sending love'},
  {text: '🥹 Awww'},
  {text: "🍬 You're the sweetest!"},
  {text: '🍾 Cheers'},
  {text: '🫂 In this together'},
  {text: '💌 Grateful'},
];

const EMOJI_CONFIG = {
  uniqueness: 1,
  position: {
    x: window.innerWidth / 2,
    y: window.innerHeight * 0.8,
  },
  emojiCount: 10,
  physics: {
    fontSize: {max: 74, min: 64},
    rotation: {max: -7, min: 7},
    initialVelocities: {
      rotation: {max: -3, min: 3},
      y: {max: -25, min: -20},
    },
    gravity: 0.2,
  },
};

type ECardActionsDrawerProps = {
  show: boolean;
  isCreator: boolean;
};

export const ECardActionsDrawer: FC<ECardActionsDrawerProps> = ({show, isCreator}) => {
  const [hasReacted, setHasReacted] = useState(false);

  const ecardId = useAppSelector(selectEcardId);
  const isDraft = useAppSelector(selectEcardIsDraft);
  const hostName = useAppSelector(selectEcardHostName);
  const hasGiftCard = useAppSelector(selectHasGiftCard);
  // Guest id should probably come from a selector, but we don't have a 'window' reducer
  const guestId = window.guest_id;

  const navigateToSuccess = async (reaction: string): Promise<void> => {
    if (guestId && ecardId) {
      try {
        await saveReaction(reaction, guestId, ecardId);
        history.push(`/success?reaction=${reaction}`);
      } catch (err) {
        setHasReacted(false);
        evite.error('Error: saveReaction failed.', err);
        toastAlert('Error reacting to card', {
          type: 'error',
          icon: <AlertLineIcon ratio={0.75} />,
        });
      }
    } else {
      if (!guestId) {
        evite.error('Error: guestId is missing.');
      } else {
        evite.error('Error: ecardId is missing.');
      }
      toastAlert('Sorry, something went wrong. Try refreshing the page.', {
        icon: <AlertLineIcon />,
        type: 'error',
        position: 'top-right',
      });
    }
  };

  const handleReaction = (reaction: string): void => {
    if (!hasReacted) {
      setHasReacted(true);

      const emoji = reaction.split(' ')[0];

      emojiBlast({
        ...EMOJI_CONFIG,
        emojis: [emoji],
      });

      setTimeout(() => {
        navigateToSuccess(reaction);
      }, 3000);
    } else {
      toastAlert("You've already reacted to this event", {
        type: 'warning',
        icon: <AlertLineIcon ratio={0.75} />,
      });
    }
  };

  return (
    <div
      className={cx('ecard__reaction-drawer', {
        'ecard__reaction-drawer--visible': show,
        reacted: window.guest_reaction,
      })}
      id="ecard__reaction-drawer"
    >
      {hasGiftCard ? <GiftCardController /> : null}
      <div className="ecard__reaction-drawer__header">
        <Typography variant="paragraph3" color="pebble">
          Reply to
        </Typography>
        <Typography variant="paragraph2" data-qa-id="from">
          {hostName}
        </Typography>
      </div>
      {window.guest_reaction ? (
        <div className="ecard__reaction-drawer__reaction-sent">
          <Typography variant="paragraph3" color="emeraldWebText">
            {window.guest_reaction.split(' ')[0]} Sent to {hostName}!
          </Typography>
          <Link href="/cards" data-qa-id="go-to-greeting-cards" disabled={isDraft}>
            Spread the love, send a Greeting Card
          </Link>
        </div>
      ) : (
        <div className="ecard__reaction-drawer__reactions">
          {BUTTONS.map((btn) =>
            btn.text ? (
              <Button
                variant="secondary"
                key={btn.text}
                className="ecard__reaction-drawer__reaction flip-box"
                onClick={() => handleReaction(btn.text)}
                data-qa-id="greeting-card-reaction-btn"
                disabled={isDraft || isCreator}
              >
                {btn.text}
              </Button>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};
